/* eslint-disable sort-keys */
import { createTheme, darken, lighten } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Theme {
    tableCT: {
      backgroundColor: string;
      hover: string;
      borderColor: string;
    };
    nav: {
      activeLinkBackGround: React.CSSProperties;
    };
    grayCT: {
      backgroundColor: string;
      hover: string;
      borderColor: string;
      color: string;
    };
  }
  // allow configuration using `createTheme`
  interface ThemeOptions {
    tableCT?: {
      backgroundColor?: string;
      hover?: string;
      borderColor?: string;
    };
    nav?: {
      activeLinkBackGround?: string;
    };
    grayCT?: {
      backgroundColor?: string;
      hover?: string;
      borderColor?: string;
      color?: string;
    };
  }

  interface TypographyVariants {
    subtitle3: React.CSSProperties;
    body3: React.CSSProperties;
    actionText: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    subtitle3?: React.CSSProperties;
    body3?: React.CSSProperties;
    actionText?: React.CSSProperties;
  }
  interface Palette {
    play: Palette['primary'];
    chipGrayCT: Palette['primary'];
    ogaModes: Palette['primary'];
    filters: {
      lessons: string;
      standards: string;
      skills: string;
    };
  }

  interface PaletteOptions {
    play: PaletteOptions['primary'];
    chipGrayCT: PaletteOptions['primary'];
    ogaModes: PaletteOptions['primary'];
    filters?: {
      lessons: string;
      standards: string;
      skills: string;
    };
  }
}
// Update the Button's color prop options
declare module '@mui/material/Chip' {
  interface ChipPropsColorOverrides {
    play: true;
    chipGrayCT: true;
  }
}
declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    play: true;
    chipGrayCT: true;
    ogaModes: true;
  }
}
declare module '@mui/material/Badge' {
  interface BadgePropsColorOverrides {
    play: true;
    chipGrayCT: true;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    subtitle3: true;
    body3: true;
    actionText: true;
  }
}
const lightGrayColor = '#8d8d8d30';
const darkGrayColor = '#4A4845';
const playColor = '#1e7d55';
const MUItheme = createTheme({
  tableCT: {
    backgroundColor: '#FFFFFF',
    hover: '#F7F7F7',
    borderColor: '#EBEBEB',
  },
  nav: {
    activeLinkBackGround: '#dee8fd',
  },
  grayCT: {
    backgroundColor: darkGrayColor,
    hover: darken(darkGrayColor, 0.1),
    borderColor: darkGrayColor,
    color: darkGrayColor,
  },
  palette: {
    primary: {
      main: '#2E5BC7',
      light: '#4786FC',
      dark: '#1d4aa0',
      contrastText: '#ffffff',
      50: '#E8F2FF',
      100: '#C5D9FF',
      200: '#9EC0FF',
      300: '#77A7FF',
      400: '#5A94FE',
      500: '#4786FC',
      600: '#3E7AFA',
      700: '#336CF6',
      800: '#2A5EF2',
      900: '#1A47EB',
    },
    secondary: {
      main: '#F6A527',
      light: '#F8B85A',
      dark: '#D67F04',
      contrastText: '#ffffff',
      50: '#FEF8E6',
      100: '#FDEEC0',
      200: '#FBE296',
      300: '#F9D66C',
      400: '#F7CA42',
      500: '#F6A527',
      600: '#F59B1D',
      700: '#F38F13',
      800: '#F18309',
      900: '#EF7100',
    },
    ogaModes: {
      main: '#ffe3c0',
      contrastText: '#000',
    },
    error: {
      main: '#C62828',
      light: '#F88078',
      dark: '#B71C1C',
      contrastText: '#ffffff',
      50: '#FFEBEE',
      100: '#FFCDD2',
      200: '#EF9A9A',
      300: '#E57373',
      400: '#EF5350',
      500: '#f44336',
      600: '#E53935',
      700: '#D32F2F',
      800: '#C62828',
      900: '#B71C1C',
    },
    warning: {
      main: '#952f2b',
      light: '#B85F5B',
      dark: '#6A1B1A',
      contrastText: '#ffffff',
      50: '#FBE9E7',
      100: '#FFCCBC',
      200: '#FFAB91',
      300: '#FF8A65',
      400: '#FF7043',
      500: '#952f2b',
      600: '#8E2A26',
      700: '#862421',
      800: '#7E1E1C',
      900: '#701417',
    },
    success: {
      main: '#2e7d32',
      light: '#81C784',
      dark: '#1b5e20',
      contrastText: '#ffffff',
      50: '#E8F5E8',
      100: '#C8E6C9',
      200: '#A5D6A7',
      300: '#81C784',
      400: '#66BB6A',
      500: '#4caf50',
      600: '#43A047',
      700: '#388E3C',
      800: '#2E7D32',
      900: '#1B5E20',
    },
    info: {
      main: '#2196f3',
      light: '#64B5F6',
      dark: '#1976D2',
      contrastText: '#ffffff',
      50: '#E3F2FD',
      100: '#BBDEFB',
      200: '#90CAF9',
      300: '#64B5F6',
      400: '#42A5F5',
      500: '#2196f3',
      600: '#1E88E5',
      700: '#1976D2',
      800: '#1565C0',
      900: '#0D47A1',
    },
    grey: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#eeeeee',
      300: '#e0e0e0',
      400: '#bdbdbd',
      500: '#9e9e9e',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
      A100: '#d5d5d5',
      A200: '#aaaaaa',
      A400: '#303030',
      A700: '#616161',
    },
    play: {
      main: playColor,
      dark: darken(playColor, 0.2),
      light: lighten(playColor, 0.2),
      contrastText: '#ffffff',
    },
    chipGrayCT: {
      main: darkGrayColor,
      dark: darken(darkGrayColor, 0.1),
      contrastText: '#ffffff',
    },
    filters: {
      lessons: '#1bd18a',
      standards: '#2E5BC7',
      skills: '#8B5700',
    },
  },
  typography: {
    button: {
      fontWeight: 500,
      fontFamily: 'Roboto',
    },
    h3: {
      fontFamily: "'Montserrat'",
      fontWeight: 600,
      fontSize: '18px',
      lineHeight: '1.5em',
      color: '#8B5700',
      letterSpacing: '-0.016em',
    },
    h4: {
      fontFamily: "'Montserrat'",
      fontWeight: 600,
      fontSize: '16px',
      lineHeight: '1.5em',
      color: '#8B5700',
      letterSpacing: '-0.032em',
    },
    h5: {
      fontFamily: "'Montserrat'",
      fontWeight: 600,
      fontSize: '14px',
      lineHeight: '1.5em',
      color: '#8B5700',
      letterSpacing: 0,
    },
    h6: {
      fontFamily: "'Montserrat'",
      fontWeight: 600,
      fontSize: '12px',
      lineHeight: '1.5em',
      color: '#8B5700',
      letterSpacing: '0.024em',
    },
    subtitle1: {
      fontFamily: '"Roboto"',
      fontWeight: 500,
      fontSize: '14px',
      lineHeight: '1.5em',
      color: '#6B6B6B',
      letterSpacing: 0,
    },
    subtitle2: {
      fontFamily: '"Roboto"',
      fontWeight: 500,
      fontSize: '20px',
      lineHeight: '1.5em',
      color: '#6B6B6B',
      letterSpacing: '-0.04em',
    },
    subtitle3: {
      fontFamily: '"Roboto"',
      fontWeight: 500,
      fontSize: '11px',
      lineHeight: '1.5em',
      color: '#6B6B6B',
      letterSpacing: '0.022em',
    },
    body1: {
      fontFamily: '"Roboto"',
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: '1.5em',
      color: '#000000',
      letterSpacing: 0,
    },
    body2: {
      fontFamily: '"Roboto"',
      fontWeight: 400,
      fontSize: '16px',
      lineHeight: '1.5em',
      color: '#000000',
      letterSpacing: 0,
    },
    body3: {
      fontFamily: '"Roboto"',
      fontWeight: 400,
      fontSize: '12px',
      lineHeight: '1.5em',
      color: '#000000',
      letterSpacing: 0,
    },
    actionText: {
      fontFamily: '"Roboto"',
      fontWeight: 500,
      fontSize: '14px',
      lineHeight: '1.5em',
      color: '#2E5BC7',
      letterSpacing: 0,
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 768,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  components: {
    MuiAppBar: {
      defaultProps: {
        color: 'secondary',
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            lineHeight: '150%',
          }),
      },
    },

    MuiDialogTitle: {
      defaultProps: {
        color: 'secondary',
      },
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start',
          }),
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            p: 1.5,
            gap: 1.5,
            display: 'flex',
            borderTop: `solid 1px ${lightGrayColor}`,
            justifyContent: 'end',
            '&>:not(:first-of-type)': {
              marginLeft: '0',
            },
          }),
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            gap: 2,
            display: 'flex',
            flexDirection: 'column',
            '& *': {
              textAlign: 'center',
            },
          }),
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            '.MuiButton-sizeSmall': {
              px: 1.2,
              py: 0.6,
            },
            '.MuiButton-sizeMedium': {
              px: 2,
              py: 1,
            },
            '.MuiButton-sizeLarge': {
              px: 3,
              py: 1.5,
            },

            textTransform: 'capitalize',
          }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            background: '#FFFFFF 0% 0% no-repeat padding-box',
            boxShadow: '0px 1px 4px #00000014',
          }),
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          marginLeft: '0px',
          marginRight: '0px',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: `
        body.debugTestId [data-testid]:hover {
          position: relative;
        }
        body.debugTestId [data-testid]:hover::after {
          content: attr(data-testid);
          position: absolute;
          top: -.5em;
          left: 0;
          background: #222;
          color: #fff;
          font-size: 8px;
          padding: 2px 8px;
          border-radius: 4px;
          white-space: nowrap;
          z-index: 9999;
          pointer-events: none;
          text-transform: none;
        }
        :focus-visible{
            outline: 2px solid ${lighten(playColor, 0.2)};
            outline-offset: 8px;
          }
      `,
    },
  },
  spacing: (factor) => `${0.5 * factor}rem`,
  shape: {
    borderRadius: 8,
  },
});
export default MUItheme;
